import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import EnhancedGrid from "../common/EnhancedGrid";
import { FaServer } from "react-icons/fa";
import Utils from "../../utils";
import { runbookServerActions } from "../../actions/runbookServer.actions";
import { MdDelete, MdEdit } from "react-icons/md";
import ConfirmModal from "../common/ConfirmModal";
import { Alert, Button } from "react-bootstrap";
import GridHelper from "../helpers/gridHelper";
import { useToast } from "../../contexts/ToastContext";

function RunbookServerGrid({ onEditServer, onAddServer, hideTitle }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const runbookServer = useSelector((state) => state.runbookServer);
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);
  const [deleteServerName, setDeleteServerName] = useState("");
  const [selectedServer, setSelectedServer] = useState(null);

  const refresh = () => {
    let queryParams = Utils.getQueryStringParams(location);

    // Default to page 1 if no page number is specified
    if (!queryParams.pageNumber) {
      queryParams.pageNumber = 1;
    }

    dispatch(runbookServerActions.get(queryParams));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [location]);

  // Refresh the grid after a create/update (e.g. from the Add/Edit window) or delete completes.
  useEffect(() => {
    if (runbookServer.success) {
      refresh();
    }
    // eslint-disable-next-line
  }, [runbookServer.success]);

  useEffect(() => {
    if (runbookServer.deleteSuccess) {
      refresh();
      setSelectedServer(null);
      showToast("Server deleted successfully", "success");
    }
    // eslint-disable-next-line
  }, [runbookServer.deleteSuccess]);

  const handleDeleteServer = () => {
    setConfirmDeleteModalShow(false);
    let requestParameters = {
      serverName: deleteServerName,
    };

    dispatch(runbookServerActions.remove(requestParameters));
  };

  const displayColumns = [
    "serverName",
    "description",
    "serverUrl",
  ];

  const data = runbookServer.paginatedResult?.data || [];

  return (
    <>
      {!hideTitle && <Title text="Runbook Servers" icon={<FaServer className="mb-1" />} />}

      {runbookServer.error && runbookServer.errorMessage && (
        <Alert variant="danger" dismissible onClose={() => dispatch({ type: "CLEAR_ERROR" })}>
          <strong>Error:</strong> {runbookServer.errorMessage}
        </Alert>
      )}

      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded"
        style={{
          opacity: selectedServer ? 1 : 0.6,
          transition: 'opacity 0.3s ease'
        }}>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedServer}
          onClick={() => {
            if (!selectedServer) return;
            if (onEditServer) {
              onEditServer(selectedServer.serverName);
            } else {
              navigate(`edit?serverName=${selectedServer.serverName}`);
            }
          }}
        >
          <MdEdit className="mb-1 me-2" />
          Edit
        </Button>

        <Button
          variant="outline-danger"
          size="sm"
          disabled={!selectedServer}
          onClick={() => {
            if (selectedServer) {
              setDeleteServerName(selectedServer.serverName);
              setConfirmDeleteModalShow(true);
            }
          }}
        >
          <MdDelete className="mb-1 me-2" />
          Delete
        </Button>

        {onAddServer ? (
          <Button variant="primary" size="sm" className="ms-auto" onClick={onAddServer}>
            Add Server
          </Button>
        ) : (
          <Link to="add" className="btn btn-primary btn-sm ms-auto">
            Add Server
          </Link>
        )}

        {selectedServer && (
          <div className="align-self-center">
            <small className="text-muted">
              Selected: <strong>{selectedServer.serverName}</strong>
            </small>
          </div>
        )}
      </div>

      <EnhancedGrid
        gridId="runbookserver"
        fillHeight={hideTitle}
        data={data}
        displayColumns={displayColumns}
        emptyState={{
          icon: <FaServer size={80} className="text-muted mb-3" />,
          message: "No servers found",
          subtitle: "Add a server to manage your runbook infrastructure.",
          action: onAddServer ? (
            <Button variant="primary" size="sm" onClick={onAddServer}>
              Add Server
            </Button>
          ) : (
            <Link to="add" className="btn btn-primary btn-sm">
              Add Server
            </Link>
          ),
        }}
        idColumn={"serverName"}
        pageNumber={(runbookServer.paginatedResult?.pageNumber ?? 1) - 1}
        totalPages={runbookServer.paginatedResult?.totalPages ?? 0}
        loading={runbookServer.loading}
        onPageChange={GridHelper.handlePageClick}
        onSort={GridHelper.handleSortClick}
        onRowClick={setSelectedServer}
        selectedRow={selectedServer}
      />

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Delete Runbook Server"
        question={`Are you sure you want to delete the server "${deleteServerName}"?`}
        noButtonText="Cancel"
        yesButtonText="Yes, Delete"
        handleClose={() => setConfirmDeleteModalShow(false)}
        handleConfirm={handleDeleteServer}
      />
    </>
  );
}

export default RunbookServerGrid;
